import { TextField, Typography } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ColorResult, HSLColor } from 'react-color';

import { HEX_COLOR_VALIDATION_REGEX } from '../../../../constants/shell';
import { KeyboardKeysEnum } from '../../../../types/shell';
import { getActionPrefix } from '../../../../utils/helpers';

interface IProps {
  value: string;
  onChange: (hexValue: string, colorResult?: ColorResult) => void;
  hsl?: HSLColor;
  alpha?: number;
  width?: number;
  height?: number;
  fontSize?: string;
  dataAction?: string;
}

export const HexInput: React.FC<IProps> = ({
  value,
  onChange,
  hsl,
  alpha = 1,
  width = 90,
  height,
  fontSize = '14px',
  dataAction,
}) => {
  const [hexInput, setHexInput] = useState<string>(() => value.replace('#', ''));
  const [isEditing, setIsEditing] = useState(false);

  const dataActionPrefix = useMemo(() => getActionPrefix(dataAction), [dataAction]);

  useEffect(() => {
    if (!isEditing) {
      const newHex = value.replace('#', '');
      if (newHex.length === 6) {
        setHexInput(newHex);
      }
    }
  }, [value, isEditing]);

  const handleHexChange = useCallback(
    (hexValue: string) => {
      if (hexValue.length === 6) {
        const hexColor = `#${hexValue}`;
        const r = parseInt(hexValue.substring(0, 2), 16);
        const g = parseInt(hexValue.substring(2, 4), 16);
        const b = parseInt(hexValue.substring(4, 6), 16);
        const colorResult: ColorResult = {
          hex: hexColor,
          rgb: { r, g, b, a: alpha },
          hsl: hsl || { h: 0, s: 0, l: 0, a: alpha },
        };
        onChange(hexColor, colorResult);
      } else {
        onChange(`#${hexValue}`);
      }
    },
    [onChange, hsl, alpha],
  );

  const onHexInputFocus = useCallback(() => {
    setIsEditing(true);
  }, []);

  const onHexInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;
      if (HEX_COLOR_VALIDATION_REGEX.test(inputValue)) {
        setHexInput(inputValue);
        if (inputValue.length === 6) {
          handleHexChange(inputValue);
        }
      }
    },
    [handleHexChange],
  );

  const onHexInputBlur = useCallback(() => {
    setIsEditing(false);
    if (hexInput.length === 6) {
      handleHexChange(hexInput);
    } else if (hexInput.length < 6 && value) {
      setHexInput(value.replace('#', ''));
    }
  }, [handleHexChange, value]);

  const onHexInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === KeyboardKeysEnum.Enter && hexInput.length === 6) {
        handleHexChange(hexInput);
        e.currentTarget.blur();
      }
    },
    [handleHexChange, hexInput],
  );

  return (
    <TextField
      size="small"
      value={hexInput}
      onFocus={onHexInputFocus}
      onChange={onHexInputChange}
      onBlur={onHexInputBlur}
      onKeyDown={onHexInputKeyDown}
      sx={{ width }}
      slotProps={{
        input: {
          sx: {
            fontSize,
            ...(height && {
              height,
              px: 1,
              py: 0.7,
              '& input': {
                p: 0,
                fontSize: 16,
              },
            }),
          },
          startAdornment: <Typography sx={{ color: 'text.secondary', p: 0 }}>#</Typography>,
        },
        htmlInput: {
          maxLength: 6,
        },
      }}
      data-action={`${dataActionPrefix}hex_input`}
    />
  );
};
